import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import {
  CustomDialogProps,
  SmartAutoComplete,
  SmartForm,
  SmartSubmit,
} from "@nodeon/react-components";
import { useSnackbar } from "notistack";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  ApiResponse,
  useOrganizationApi,
  UserListDto,
} from "~/api/GeneratedApi";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";

interface FormProps {
  roleIds: number[];
}

interface Props {
  organizationId: number;
  user: UserListDto;
  onSubmitted?: () => void;
}

const OrganizationUserEditDialog = ({
  handleClose,
  onSubmitted,
  organizationId,
  user,
}: CustomDialogProps & Props): JSX.Element => {
  const organizationApi = useOrganizationApi();
  const { t } = useTranslation("organization-user-edit");
  const { enqueueSnackbar } = useSnackbar();

  const { isLoading, data: roles } = useCustomQuery(
    { key: "organization-roles", id: organizationId },
    () => organizationApi.getOrganizationRoles(organizationId),
  );

  const onSuccess = () => {
    enqueueSnackbar(t("role-changed", { user: user.name }), {
      variant: "success",
    });
    onSubmitted?.();
    handleClose?.();
  };

  const onSubmit = (data: FormProps) => {
    return organizationApi.editOrganizationUser(organizationId, user.id, {
      roleIDs: data.roleIds,
    });
  };

  return (
    <Dialog open onClose={handleClose} aria-labelledby="form-dialog-title">
      <SmartForm<FormProps, ApiResponse>
        onSuccess={onSuccess}
        onSubmit={onSubmit}
        noGrid
        formProps={{
          defaultValues: {
            roleIds: user?.organizationRoles?.map((r) => r.id) ?? [],
          },
        }}
      >
        <DialogTitle id="form-dialog-title">
          {t("title", { user: user.name })}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SmartAutoComplete<FormProps, number, true>
                options={roles?.results.map((x) => x.id) ?? []}
                fullWidth
                disabled={isLoading}
                name="roleIds"
                multiple
                label={t("role")}
                getOptionLabel={(opt) =>
                  roles?.results.find((x) => x.id === opt)?.roleName ??
                  t("general:unknown")
                }
                controllerProps={{
                  rules: {
                    validate: (val) => {
                      if (typeof val === "number" || !val)
                        return t("user-already-in-role").toString();
                      if (val.length < 1) return t("no-role-given").toString();
                      return true;
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            {t("general:cancel")}
          </Button>
          <SmartSubmit>{t("general:save")}</SmartSubmit>
        </DialogActions>
      </SmartForm>
    </Dialog>
  );
};

export default OrganizationUserEditDialog;
